import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  Divider,
  Switch,
  FormControlLabel,
  MenuItem,
  Snackbar,
  Alert,
  Avatar,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Hotel,
  AttachMoney,
  Notifications,
  Security,
  Business,
  Email,
  Save,
  RestartAlt,
  Edit,
} from '@mui/icons-material';

export default function Settings() {
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [hotelSettings, setHotelSettings] = useState({
    name: 'HotelOpsPro',
    address: '123 Hotel Street, New York, NY 10001',
    phone: '(555) 123-4567',
    email: 'info@hotelops.com',
    website: 'www.hotelops.com',
    checkInTime: '15:00',
    checkOutTime: '11:00',
    currency: 'USD',
    timezone: 'America/New_York',
  });

  const [roomRates, setRoomRates] = useState({
    standard: 120,
    deluxe: 180,
    suite: 280,
    family: 220,
    weekendSurcharge: 20,
    peakSeasonMultiplier: 1.3,
  });

  const [taxSettings, setTaxSettings] = useState({
    taxRate: 10,
    serviceFee: 5,
    includeTaxInPrice: false,
    taxName: 'Sales Tax',
  });

  const [policies, setPolicies] = useState({
    cancellationHours: 48,
    cancellationFee: 1,
    earlyCheckInFee: 30,
    lateCheckOutFee: 30,
    petPolicy: false,
    smokingPolicy: false,
    maxGuestsPerRoom: 4,
  });

  const [notifications, setNotifications] = useState({
    emailNotifications: true,
    smsNotifications: false,
    bookingConfirmation: true,
    checkInReminder: true,
    checkOutReminder: true,
    paymentReceipt: true,
    promotionalEmails: false,
  });

  const [emailSettings, setEmailSettings] = useState({
    smtpHost: 'smtp.gmail.com',
    smtpPort: 587,
    smtpUser: 'noreply@hotelops.com',
    fromName: 'HotelOpsPro',
    replyTo: 'support@hotelops.com',
  });

  const handleSave = (section) => {
    setSnackbar({
      open: true,
      message: `${section} settings saved successfully!`,
      severity: 'success',
    });
  };

  const handleReset = (section) => {
    setSnackbar({
      open: true,
      message: `${section} settings reset to defaults`,
      severity: 'info',
    });
  };

  const tabs = [
    { label: 'General', icon: <Hotel /> },
    { label: 'Pricing', icon: <AttachMoney /> },
    { label: 'Policies', icon: <Security /> },
    { label: 'Notifications', icon: <Notifications /> },
    { label: 'Email', icon: <Email /> },
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box mb={3}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Settings & Configuration
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage your hotel settings, rates, and policies
        </Typography>
      </Box>

      <Paper>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {tabs.map((tab, index) => (
            <Tab key={index} label={tab.label} icon={tab.icon} iconPosition="start" />
          ))}
        </Tabs>

        <Box p={3}>
          {/* General Settings */}
          {activeTab === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <Avatar sx={{ bgcolor: '#1976d2', width: 56, height: 56 }}>
                    <Business />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Hotel Information
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Basic information about your property
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Hotel Name"
                  value={hotelSettings.name}
                  onChange={(e) => setHotelSettings({ ...hotelSettings, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={hotelSettings.phone}
                  onChange={(e) => setHotelSettings({ ...hotelSettings, phone: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={hotelSettings.email}
                  onChange={(e) => setHotelSettings({ ...hotelSettings, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Website"
                  value={hotelSettings.website}
                  onChange={(e) => setHotelSettings({ ...hotelSettings, website: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  multiline
                  rows={2}
                  value={hotelSettings.address}
                  onChange={(e) => setHotelSettings({ ...hotelSettings, address: e.target.value })}
                />
              </Grid>

              <Grid item xs={12}><Divider /></Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Check-in Time"
                  type="time"
                  value={hotelSettings.checkInTime}
                  onChange={(e) => setHotelSettings({ ...hotelSettings, checkInTime: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Check-out Time"
                  type="time"
                  value={hotelSettings.checkOutTime}
                  onChange={(e) => setHotelSettings({ ...hotelSettings, checkOutTime: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  select
                  label="Currency"
                  value={hotelSettings.currency}
                  onChange={(e) => setHotelSettings({ ...hotelSettings, currency: e.target.value })}
                >
                  <MenuItem value="USD">USD - US Dollar</MenuItem>
                  <MenuItem value="EUR">EUR - Euro</MenuItem>
                  <MenuItem value="GBP">GBP - British Pound</MenuItem>
                  <MenuItem value="JPY">JPY - Japanese Yen</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  select
                  label="Timezone"
                  value={hotelSettings.timezone}
                  onChange={(e) => setHotelSettings({ ...hotelSettings, timezone: e.target.value })}
                >
                  <MenuItem value="America/New_York">Eastern Time</MenuItem>
                  <MenuItem value="America/Chicago">Central Time</MenuItem>
                  <MenuItem value="America/Denver">Mountain Time</MenuItem>
                  <MenuItem value="America/Los_Angeles">Pacific Time</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" gap={2}>
                  <Button variant="contained" startIcon={<Save />} onClick={() => handleSave('General')}>
                    Save Changes
                  </Button>
                  <Button variant="outlined" startIcon={<RestartAlt />} onClick={() => handleReset('General')}>
                    Reset to Defaults
                  </Button>
                </Box>
              </Grid>
            </Grid>
          )}

          {/* Pricing Settings */}
          {activeTab === 1 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <Avatar sx={{ bgcolor: '#2e7d32', width: 56, height: 56 }}>
                    <AttachMoney />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Room Rates & Pricing
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Configure base room rates and pricing rules
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ bgcolor: '#f5f5f5' }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Base Room Rates (per night)
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Standard Room"
                          type="number"
                          value={roomRates.standard}
                          onChange={(e) => setRoomRates({ ...roomRates, standard: parseFloat(e.target.value) })}
                          InputProps={{ startAdornment: <Typography mr={1}>$</Typography> }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Deluxe Room"
                          type="number"
                          value={roomRates.deluxe}
                          onChange={(e) => setRoomRates({ ...roomRates, deluxe: parseFloat(e.target.value) })}
                          InputProps={{ startAdornment: <Typography mr={1}>$</Typography> }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Suite"
                          type="number"
                          value={roomRates.suite}
                          onChange={(e) => setRoomRates({ ...roomRates, suite: parseFloat(e.target.value) })}
                          InputProps={{ startAdornment: <Typography mr={1}>$</Typography> }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Family Room"
                          type="number"
                          value={roomRates.family}
                          onChange={(e) => setRoomRates({ ...roomRates, family: parseFloat(e.target.value) })}
                          InputProps={{ startAdornment: <Typography mr={1}>$</Typography> }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ bgcolor: '#f5f5f5' }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Dynamic Pricing
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Weekend Surcharge"
                          type="number"
                          value={roomRates.weekendSurcharge}
                          onChange={(e) => setRoomRates({ ...roomRates, weekendSurcharge: parseFloat(e.target.value) })}
                          InputProps={{ startAdornment: <Typography mr={1}>$</Typography> }}
                          helperText="Additional charge for Fri-Sat nights"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Peak Season Multiplier"
                          type="number"
                          value={roomRates.peakSeasonMultiplier}
                          onChange={(e) => setRoomRates({ ...roomRates, peakSeasonMultiplier: parseFloat(e.target.value) })}
                          inputProps={{ step: 0.1, min: 1 }}
                          helperText="Multiply base rate by this value"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                <Card sx={{ bgcolor: '#f5f5f5', mt: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Tax & Fees
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Tax Rate"
                          type="number"
                          value={taxSettings.taxRate}
                          onChange={(e) => setTaxSettings({ ...taxSettings, taxRate: parseFloat(e.target.value) })}
                          InputProps={{ endAdornment: <Typography ml={1}>%</Typography> }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Service Fee"
                          type="number"
                          value={taxSettings.serviceFee}
                          onChange={(e) => setTaxSettings({ ...taxSettings, serviceFee: parseFloat(e.target.value) })}
                          InputProps={{ endAdornment: <Typography ml={1}>%</Typography> }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={taxSettings.includeTaxInPrice}
                              onChange={(e) => setTaxSettings({ ...taxSettings, includeTaxInPrice: e.target.checked })}
                            />
                          }
                          label="Include tax in displayed prices"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" gap={2}>
                  <Button variant="contained" startIcon={<Save />} onClick={() => handleSave('Pricing')}>
                    Save Changes
                  </Button>
                  <Button variant="outlined" startIcon={<RestartAlt />} onClick={() => handleReset('Pricing')}>
                    Reset to Defaults
                  </Button>
                </Box>
              </Grid>
            </Grid>
          )}

          {/* Policies */}
          {activeTab === 2 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <Avatar sx={{ bgcolor: '#ed6c02', width: 56, height: 56 }}>
                    <Security />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Hotel Policies
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Configure booking and guest policies
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Cancellation Period (hours)"
                  type="number"
                  value={policies.cancellationHours}
                  onChange={(e) => setPolicies({ ...policies, cancellationHours: parseInt(e.target.value) })}
                  helperText="Free cancellation available until this many hours before check-in"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Cancellation Fee (nights)"
                  type="number"
                  value={policies.cancellationFee}
                  onChange={(e) => setPolicies({ ...policies, cancellationFee: parseInt(e.target.value) })}
                  helperText="Charge this many nights for late cancellations"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Early Check-in Fee"
                  type="number"
                  value={policies.earlyCheckInFee}
                  onChange={(e) => setPolicies({ ...policies, earlyCheckInFee: parseFloat(e.target.value) })}
                  InputProps={{ startAdornment: <Typography mr={1}>$</Typography> }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Late Check-out Fee"
                  type="number"
                  value={policies.lateCheckOutFee}
                  onChange={(e) => setPolicies({ ...policies, lateCheckOutFee: parseFloat(e.target.value) })}
                  InputProps={{ startAdornment: <Typography mr={1}>$</Typography> }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Maximum Guests Per Room"
                  type="number"
                  value={policies.maxGuestsPerRoom}
                  onChange={(e) => setPolicies({ ...policies, maxGuestsPerRoom: parseInt(e.target.value) })}
                />
              </Grid>

              <Grid item xs={12}><Divider /></Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={policies.petPolicy}
                      onChange={(e) => setPolicies({ ...policies, petPolicy: e.target.checked })}
                    />
                  }
                  label="Pets Allowed"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={policies.smokingPolicy}
                      onChange={(e) => setPolicies({ ...policies, smokingPolicy: e.target.checked })}
                    />
                  }
                  label="Smoking Allowed"
                />
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" gap={2}>
                  <Button variant="contained" startIcon={<Save />} onClick={() => handleSave('Policies')}>
                    Save Changes
                  </Button>
                  <Button variant="outlined" startIcon={<RestartAlt />} onClick={() => handleReset('Policies')}>
                    Reset to Defaults
                  </Button>
                </Box>
              </Grid>
            </Grid>
          )}

          {/* Notifications */}
          {activeTab === 3 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <Avatar sx={{ bgcolor: '#9c27b0', width: 56, height: 56 }}>
                    <Notifications />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Notification Preferences
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Configure automated notifications to guests
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ bgcolor: '#f5f5f5' }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Notification Channels
                    </Typography>
                    <List>
                      <ListItem>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={notifications.emailNotifications}
                              onChange={(e) => setNotifications({ ...notifications, emailNotifications: e.target.checked })}
                            />
                          }
                          label="Email Notifications"
                        />
                      </ListItem>
                      <ListItem>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={notifications.smsNotifications}
                              onChange={(e) => setNotifications({ ...notifications, smsNotifications: e.target.checked })}
                            />
                          }
                          label="SMS Notifications"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ bgcolor: '#f5f5f5' }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Automated Notifications
                    </Typography>
                    <List>
                      <ListItem>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={notifications.bookingConfirmation}
                              onChange={(e) => setNotifications({ ...notifications, bookingConfirmation: e.target.checked })}
                            />
                          }
                          label="Booking Confirmation"
                        />
                      </ListItem>
                      <ListItem>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={notifications.checkInReminder}
                              onChange={(e) => setNotifications({ ...notifications, checkInReminder: e.target.checked })}
                            />
                          }
                          label="Check-in Reminder (24h before)"
                        />
                      </ListItem>
                      <ListItem>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={notifications.checkOutReminder}
                              onChange={(e) => setNotifications({ ...notifications, checkOutReminder: e.target.checked })}
                            />
                          }
                          label="Check-out Reminder"
                        />
                      </ListItem>
                      <ListItem>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={notifications.paymentReceipt}
                              onChange={(e) => setNotifications({ ...notifications, paymentReceipt: e.target.checked })}
                            />
                          }
                          label="Payment Receipts"
                        />
                      </ListItem>
                      <ListItem>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={notifications.promotionalEmails}
                              onChange={(e) => setNotifications({ ...notifications, promotionalEmails: e.target.checked })}
                            />
                          }
                          label="Promotional Emails"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" gap={2}>
                  <Button variant="contained" startIcon={<Save />} onClick={() => handleSave('Notifications')}>
                    Save Changes
                  </Button>
                  <Button variant="outlined" startIcon={<RestartAlt />} onClick={() => handleReset('Notifications')}>
                    Reset to Defaults
                  </Button>
                </Box>
              </Grid>
            </Grid>
          )}

          {/* Email Settings */}
          {activeTab === 4 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <Avatar sx={{ bgcolor: '#d32f2f', width: 56, height: 56 }}>
                    <Email />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Email Configuration
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Configure SMTP settings for outgoing emails
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="SMTP Host"
                  value={emailSettings.smtpHost}
                  onChange={(e) => setEmailSettings({ ...emailSettings, smtpHost: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="SMTP Port"
                  type="number"
                  value={emailSettings.smtpPort}
                  onChange={(e) => setEmailSettings({ ...emailSettings, smtpPort: parseInt(e.target.value) })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="SMTP Username"
                  value={emailSettings.smtpUser}
                  onChange={(e) => setEmailSettings({ ...emailSettings, smtpUser: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="From Name"
                  value={emailSettings.fromName}
                  onChange={(e) => setEmailSettings({ ...emailSettings, fromName: e.target.value })}
                  helperText="Name shown in recipient's inbox"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Reply-To Address"
                  type="email"
                  value={emailSettings.replyTo}
                  onChange={(e) => setEmailSettings({ ...emailSettings, replyTo: e.target.value })}
                />
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" gap={2}>
                  <Button variant="contained" startIcon={<Save />} onClick={() => handleSave('Email')}>
                    Save Changes
                  </Button>
                  <Button variant="outlined" startIcon={<RestartAlt />} onClick={() => handleReset('Email')}>
                    Reset to Defaults
                  </Button>
                </Box>
              </Grid>
            </Grid>
          )}
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
